import React from 'react';
import { Badge } from '@components/atoms/Badge';
import { User, UserOrganizationRoleStatus } from '@models/user.model';
import { getTranslatedRoleLabel } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';

/** Non-ACTIVE memberships are called out; ACTIVE is the norm and stays quiet. */
const STATUS_NOTE: Partial<Record<UserOrganizationRoleStatus, string>> = {
  [UserOrganizationRoleStatus.DISABLED]: 'Disabled',
  [UserOrganizationRoleStatus.READ_ONLY]: 'Read Only',
  [UserOrganizationRoleStatus.OVERDUE]: 'Overdue',
};

interface UserMembershipsCellProps {
  user: User;
}

/**
 * Every organization a user belongs to, with the role held in each.
 *
 * Falls back to the single default organization when the server did not send
 * the full set — it only does so for SUPER_ADMIN callers.
 */
export const UserMembershipsCell: React.FC<UserMembershipsCellProps> = ({
  user,
}) => {
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');
  const memberships = [...(user.organizationRoles ?? [])].sort((a, b) =>
    (a.organization?.name ?? '').localeCompare(b.organization?.name ?? ''),
  );

  if (!memberships.length) {
    if (!user.organization?.name) {
      return (
        <span className="text-gray-400 dark:text-gray-500">
          No organization
        </span>
      );
    }
    return (
      <span className="block truncate" title={user.organization.name}>
        {user.organization.name}
        {user.role && (
          <span className="text-gray-500 dark:text-gray-400">
            {' '}
            · {getTranslatedRoleLabel(user.role, tEnumLabels, tCommon.unknown)}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {memberships.map((membership) => {
        const note = membership.status
          ? STATUS_NOTE[membership.status]
          : undefined;
        return (
          <div
            key={membership.organizationId}
            className="flex min-w-0 items-center gap-2">
            <span
              className="truncate font-medium"
              title={membership.organization?.name}>
              {membership.organization?.name || membership.organizationId}
            </span>
            <Badge size="sm" variant="primary" className="shrink-0">
              {getTranslatedRoleLabel(membership.role, tEnumLabels, tCommon.unknown)}
            </Badge>
            {note && (
              <Badge size="sm" variant="warning" className="shrink-0">
                {note}
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default UserMembershipsCell;
